import { PlanningPeriodDetailDocument } from 'generated/resources/graphql'
import { Pencil, People } from '@factorialco/f0-react/icons/app'
import buildUrl from 'modules/core/lib/buildUrl'

import i18n from 'modules/core/lib/i18n'
import type { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import type { ResourceMeta } from 'modules/core/lib/navigation/types'
import {
  getPeriodStatusLabel,
  PLANNING_PERIOD_STATUS_COLORS,
} from 'modules/planning/consts/period_status'
import {
  getRequestStatusLabel,
  REQUEST_STATUS_COLORS,
} from 'modules/planning/consts/request_status'
import {
  formatPeriod,
  getRequestDeadline,
  getRequestStatus,
} from 'modules/planning/lib/periodHelpers'

interface PeriodDetailPaths {
  periodDetailFullPath: (params: { periodId: string }) => string
  employeeSheetFullPath: (params: { periodId: string }) => string
}

export function buildPeriodDetailMeta(
  context: MetaContext,
  paths: PeriodDetailPaths
): MetaValue<ResourceMeta> {
  const { watchQuery, params } = context
  const periodId = params['periodId']

  if (!periodId) return { title: '' }

  return watchQuery({
    query: PlanningPeriodDetailDocument,
    variables: { id: String(periodId) },
  }).map(({ data }) => {
    const period = data?.planning?.period
    if (!period) return { title: '' }

    const requestStatus = getRequestStatus(period)

    return {
      title: period.name,
      subNavigation: [
        {
          title: i18n.t('planning.period_detail.tabs.overview'),
          href: paths.periodDetailFullPath({ periodId: String(periodId) }),
          index: true,
        },
        {
          title: i18n.t('planning.period_detail.tabs.employee_sheet'),
          href: paths.employeeSheetFullPath({ periodId: String(periodId) }),
        },
      ],
      headerComponentProps: {
        title: period.name,
        secondaryActions: [
          {
            key: 'edit-period',
            label: i18n.t('planning.period_detail.edit'),
            icon: Pencil,
          },
          {
            key: 'manage-collaborators',
            label: i18n.t('planning.manage_collaborators.action_label'),
            icon: People,
          },
        ],
        status: {
          label: i18n.t('planning.period_detail.status'),
          text: getPeriodStatusLabel(period.status),
          variant: PLANNING_PERIOD_STATUS_COLORS[period.status],
        },
        metadata: [
          {
            label: i18n.t('planning.period_detail.period'),
            value: {
              type: 'text' as const,
              content: formatPeriod(period),
            },
          },
          {
            label: i18n.t('planning.period_detail.request_deadline'),
            value: {
              type: 'text' as const,
              content: getRequestDeadline(period) ?? '-',
            },
          },
          {
            label: i18n.t('planning.period_detail.request_status'),
            value: {
              label: getRequestStatusLabel(requestStatus),
              type: 'status' as const,
              variant: REQUEST_STATUS_COLORS[requestStatus],
            },
          },
          ...(period.participantsConnection.edges.length > 0
            ? [
                {
                  label: i18n.t('planning.period_detail.collaborators'),
                  value: {
                    avatars: period.participantsConnection.edges.map((edge) => ({
                      type: 'person' as const,
                      firstName: edge.node.employee.firstName,
                      lastName: edge.node.employee.lastName,
                      src: buildUrl(edge.node.employee.avatar?.url),
                    })),

                    type: 'list' as const,
                    variant: 'person' as const,
                  },
                },
              ]
            : []),
          ...(period.plannersConnection.edges.length > 0
            ? [
                {
                  label: i18n.t('planning.period_detail.headcount_planners'),
                  value: {
                    avatars: period.plannersConnection.edges.map((edge) => ({
                      type: 'person' as const,
                      firstName: edge.node.employee.firstName,
                      lastName: edge.node.employee.lastName,
                      src: buildUrl(edge.node.employee.avatar?.url),
                    })),
                    type: 'list' as const,
                    variant: 'person' as const,
                  },
                },
              ]
            : []),
        ],
        primaryAction: {
          key: 'submission-period',
          label: period.submissionPeriods?.[0]
            ? i18n.t('planning.submission_period.edit_button')
            : i18n.t('planning.submission_period.create_button'),
        },
      },
    }
  })
}
